// How a video ends up: its format, its sound, its footage and its Director, in
// one place. The composer opens it before a video starts; the project sidebar
// lists every choice afterwards and any of them opens it again.
import { useEffect, useState } from "react";
import {
  AudioLines,
  Captions,
  ChevronRight,
  Clapperboard,
  Feather,
  Film,
  Music2,
  Sparkles,
  type LucideIcon,
} from "lucide-react";
import { api, patch } from "./api";
import { Button, Modal } from "./components";
import { useWallet } from "./Credits";
import { voiceCharacter, VoicePicker, voiceName } from "./Voices";
import { bilingual, lang, text, useText } from "./i18n";
import {
  CATALOG_TEXT,
  creditNumber as formatCredits,
  DEFAULT_CHOICES,
  DIRECTOR_MODELS,
  INFERENCE_CATALOG,
  MEDIA_CATALOG,
} from "../shared/catalog";
import type {
  DirectorModel,
  FootageUse,
  MusicSource,
  Project,
  VideoChoices,
} from "../shared/types";

const TEXT = bilingual({
  en: {
    choice: {
      auto: "Director's choice",
      seconds: (n: number) => `${n} s`,
      about: (n: number) => `About ${n} s`,
      // Display names of the stored language values, which stay English.
      languages: {
        English: "English",
        Spanish: "Spanish",
        French: "French",
        German: "German",
        Italian: "Italian",
        Portuguese: "Portuguese",
      } as Record<string, string>,
      footage: "AI footage",
      noFootage: "No AI footage",
      notes: "Notes",
    },
    music: {
      library: "Library song",
      included: "Included",
      original: "Original song",
      originalNote: (n: number) => `+${n} credits per 30 s`,
    },
    footageUse: { director: "Director's choice", yes: "Yes", no: "No" },
    summary: {
      length: "Length",
      language: "Language",
      voice: "Voice",
      music: "Music",
      footage: "AI footage",
      off: "Off",
      upTo: (credits: string) => `Yes · up to ${credits} credits`,
      director: "Director",
      notes: "Notes",
      edit: (label: string, value: string) =>
        `${label}: ${value}. Edit video settings`,
    },
    sheet: {
      title: "Video settings",
      video: "The video",
      length: "Length",
      language: "Language",
      director: "Director",
      notes: "Creative notes",
      optional: "optional",
      notesHint: "A mood, a reference, something to lean into.",
      sound: "Sound",
      voice: "Voice",
      matched: "Matched to each video",
      label: "AI-voice label",
      labelNote: "“AI-generated voice” at the start",
      music: "Music",
      footage: "Footage",
      footageUse: "AI-generated footage",
      perClip: (credits: number) => `${credits} credits per clip`,
      allowance: "Project footage allowance",
      credits: "credits",
      preset: (credits: number) => `${credits} credits`,
      clips: (n: number) => `≈ ${n} clips`,
      used: (spent: string, reserved: string) =>
        `${spent} used · ${reserved} reserved here`,
      available: (credits: string) => `${credits} credits available`,
      saving: "Saving…",
      save: "Save settings",
      preview: "Preview · nothing is generated.",
    },
  },
  es: {
    choice: {
      auto: "Decide el director",
      seconds: (n: number) => `${n} s`,
      about: (n: number) => `Unos ${n} s`,
      languages: {
        English: "Inglés",
        Spanish: "Español",
        French: "Francés",
        German: "Alemán",
        Italian: "Italiano",
        Portuguese: "Portugués",
      } as Record<string, string>,
      footage: "Clips con IA",
      noFootage: "Sin clips con IA",
      notes: "Notas",
    },
    music: {
      library: "Canción de la biblioteca",
      included: "Incluida",
      original: "Canción original",
      originalNote: (n: number) => `+${n} créditos cada 30 s`,
    },
    footageUse: { director: "Decide el director", yes: "Sí", no: "No" },
    summary: {
      length: "Duración",
      language: "Idioma",
      voice: "Voz",
      music: "Música",
      footage: "Clips con IA",
      off: "No",
      upTo: (credits: string) => `Sí · hasta ${credits} créditos`,
      director: "Director",
      notes: "Notas",
      edit: (label: string, value: string) =>
        `${label}: ${value}. Editar ajustes del vídeo`,
    },
    sheet: {
      title: "Ajustes del vídeo",
      video: "El vídeo",
      length: "Duración",
      language: "Idioma",
      director: "Director",
      notes: "Notas creativas",
      optional: "opcional",
      notesHint: "Un tono, una referencia, algo que destacar.",
      sound: "Sonido",
      voice: "Voz",
      matched: "Adaptada a cada vídeo",
      label: "Etiqueta de voz con IA",
      labelNote: "«Voz generada con IA» al principio",
      music: "Música",
      footage: "Clips",
      footageUse: "Clips generados con IA",
      perClip: (credits: number) => `${credits} créditos por clip`,
      allowance: "Presupuesto para clips del proyecto",
      credits: "créditos",
      preset: (credits: number) => `${credits} créditos`,
      clips: (n: number) => `≈ ${n} clips`,
      used: (spent: string, reserved: string) =>
        `${spent} usados · ${reserved} reservados aquí`,
      available: (credits: string) => `${credits} créditos disponibles`,
      saving: "Guardando…",
      save: "Guardar ajustes",
      preview: "Vista previa · no se genera nada.",
    },
  },
});

// Stored settings stay English (the Director reads them); the sheet shows
// each value's name in the visitor's language.
const AUTO = "Let the director decide";
const LENGTHS = [
  AUTO,
  "About 15 seconds",
  "About 30 seconds",
  "About 60 seconds",
] as const;
const LANGUAGES = [
  "English",
  "Spanish",
  "French",
  "German",
  "Italian",
  "Portuguese",
  AUTO,
];
/** A stored length, short ("15 s") or as a summary ("About 15 s"). */
function lengthName(v: string, short: boolean) {
  const t = text(TEXT).choice;
  if (v === AUTO) return t.auto;
  const seconds = /^About (\d+) seconds$/.exec(v)?.[1];
  if (seconds) return short ? t.seconds(+seconds) : t.about(+seconds);
  return short
    ? v.replace("About ", "").replace(" seconds", " s")
    : v.replace(" seconds", " s");
}
const languageName = (v: string) =>
  v === AUTO ? text(TEXT).choice.auto : (text(TEXT).choice.languages[v] ?? v);
/** Credits as the visitor writes numbers (1500 and 2,5 in Spain). */
const creditNumber = (n: number) => formatCredits(n, lang());

type Option<T> = { value: T; label: string; note: string; Icon: LucideIcon };
const musicOptions = (): Option<MusicSource>[] => {
  const t = text(TEXT).music;
  return [
    { value: "library", label: t.library, note: t.included, Icon: Music2 },
    {
      value: "original",
      label: t.original,
      note: t.originalNote(MEDIA_CATALOG.music.credits),
      Icon: Sparkles,
    },
  ];
};
const FOOTAGE_OPTIONS: FootageUse[] = ["director", "yes", "no"];
const DIRECTOR_ICONS: Record<DirectorModel, LucideIcon> = {
  foco: Clapperboard,
  lite: Feather,
};
const directors = () =>
  DIRECTOR_MODELS.map((m): Option<DirectorModel> => ({
    value: m,
    label: INFERENCE_CATALOG.models[m].name,
    note: text(CATALOG_TEXT).models[m].note,
    Icon: DIRECTOR_ICONS[m],
  }));

/** The choices that differ from a new video's, a word or two each. */
export function changedChoices(c: VideoChoices) {
  const d = DEFAULT_CHOICES,
    t = text(TEXT).choice;
  return [
    c.settings.length !== d.settings.length &&
      lengthName(c.settings.length, true),
    c.settings.language !== d.settings.language &&
      languageName(c.settings.language),
    voiceName(c.voice),
    c.music !== d.music && text(TEXT).music.original,
    c.footage.use !== d.footage.use &&
      (c.footage.use === "yes" ? t.footage : t.noFootage),
    c.model !== d.model && INFERENCE_CATALOG.models[c.model].name,
    !!c.settings.direction.trim() && t.notes,
  ].filter((x): x is string => !!x);
}

/** The project's choices at a glance; each row opens the sheet. */
export function VideoSummary({
  project,
  onOpen,
}: {
  project: Project;
  onOpen: () => void;
}) {
  const all = useText(TEXT),
    t = all.summary;
  const { length, language, direction } = project.settings;
  const rows: [string, string][] = [
    [t.length, lengthName(length, false)],
    [t.language, languageName(language)],
    [t.voice, voiceName(project.voice) ?? all.choice.auto],
    [
      t.music,
      project.music === "original" ? all.music.original : all.music.library,
    ],
    [
      t.footage,
      project.footage.use === "no"
        ? t.off
        : project.footage.use === "yes"
          ? t.upTo(creditNumber(project.footage.limit))
          : all.choice.auto,
    ],
    [t.director, INFERENCE_CATALOG.models[project.model].name],
    ...(direction.trim()
      ? [[t.notes, direction.trim()] as [string, string]]
      : []),
  ];
  return (
    <div className="vset">
      {rows.map(([label, value]) => (
        <button
          key={label}
          className="vset-row"
          onClick={onOpen}
          aria-label={t.edit(label, value)}
        >
          <span>{label}</span>
          <b>{value}</b>
          <ChevronRight size={14} />
        </button>
      ))}
    </div>
  );
}

/** The whole sheet, for a video being made or one still to start. */
export function VideoSettings({
  value,
  projectId,
  preview,
  onSave,
  onClose,
}: {
  value: VideoChoices;
  /** An existing video: the footage it already used bounds the allowance. */
  projectId?: string;
  preview: boolean;
  onSave: (choices: VideoChoices) => Promise<void> | void;
  onClose: () => void;
}) {
  const all = useText(TEXT),
    t = all.sheet;
  const [length, setLength] = useState(value.settings.length),
    [language, setLanguage] = useState(value.settings.language),
    [direction, setDirection] = useState(value.settings.direction),
    [model, setModel] = useState(value.model),
    [use, setUse] = useState(value.footage.use),
    [limit, setLimit] = useState(String(value.footage.limit)),
    [music, setMusic] = useState<MusicSource>(value.music),
    [voice, setVoice] = useState(value.voice),
    [label, setLabel] = useState(value.voiceLabel),
    [choosing, setChoosing] = useState(false),
    [usage, setUsage] = useState({ spent: 0, reserved: 0 }),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const { wallet } = useWallet();
  useEffect(() => {
    if (!projectId) return;
    void api(`/projects/${projectId}/production`)
      .then((d) => setUsage(d.usage))
      .catch((e) => setError(e.message));
  }, [projectId]);
  const used = usage.spent + usage.reserved;
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await onSave({
        settings: { length, language, direction },
        footage: { use, limit: Number(limit) },
        music,
        voice,
        voiceLabel: label,
        model,
      });
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={t.title} onClose={onClose}>
      <form className="stack video-settings" onSubmit={save}>
        <section className="vs-group" aria-labelledby="vs-video">
          <h3 id="vs-video">{t.video}</h3>
          <div className="field">
            <span id="vs-length">{t.length}</span>
            <div
              className="seg vs-length"
              role="group"
              aria-labelledby="vs-length"
            >
              {LENGTHS.map((value) => (
                <button
                  type="button"
                  key={value}
                  aria-pressed={length === value}
                  onClick={() => setLength(value)}
                >
                  {lengthName(value, true)}
                </button>
              ))}
            </div>
          </div>
          <label className="field">
            {t.language}
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
            >
              {LANGUAGES.map((l) => (
                <option key={l} value={l}>
                  {languageName(l)}
                </option>
              ))}
            </select>
          </label>
          <Cards
            legend={t.director}
            options={directors()}
            value={model}
            onChange={setModel}
          />
          <label className="field">
            <span>
              {t.notes} <small>{t.optional}</small>
            </span>
            <textarea
              value={direction}
              onChange={(e) => setDirection(e.target.value)}
              placeholder={t.notesHint}
              maxLength={5000}
            />
          </label>
        </section>
        <section className="vs-group" aria-labelledby="vs-sound">
          <h3 id="vs-sound">{t.sound}</h3>
          <button
            type="button"
            className="voice-row"
            onClick={() => setChoosing(true)}
          >
            <span className="voice-row-icon">
              <AudioLines size={18} />
            </span>
            <span className="voice-row-text">
              <b>
                {t.voice} · {voiceName(voice) ?? all.choice.auto}
              </b>
              <small>{voiceCharacter(voice) ?? t.matched}</small>
            </span>
            <ChevronRight size={16} />
          </button>
          {choosing && (
            <VoicePicker
              value={voice}
              onChoose={setVoice}
              onClose={() => setChoosing(false)}
            />
          )}
          <div className="footage">
            <div className="footage-row">
              <span className="footage-icon">
                <Captions size={18} />
              </span>
              <span className="footage-t">
                <b>{t.label}</b>
                <small>{t.labelNote}</small>
              </span>
              <button
                type="button"
                role="switch"
                aria-checked={label}
                aria-label={t.label}
                className="switch"
                onClick={() => setLabel(!label)}
              />
            </div>
          </div>
          <Cards
            legend={t.music}
            options={musicOptions()}
            value={music}
            onChange={setMusic}
          />
        </section>
        <section className="vs-group" aria-labelledby="vs-footage">
          <h3 id="vs-footage">{t.footage}</h3>
          <div className={`footage ${use === "yes" ? "on" : ""}`}>
            <div className="footage-row">
              <span className="footage-icon">
                <Film size={18} />
              </span>
              <span className="footage-t">
                <b id="vs-footage-use">{t.footageUse}</b>
                <small>
                  {MEDIA_CATALOG.video.name} ·{" "}
                  {t.perClip(MEDIA_CATALOG.video.credits)}
                </small>
              </span>
            </div>
            <div
              className="seg footage-use"
              role="group"
              aria-labelledby="vs-footage-use"
            >
              {FOOTAGE_OPTIONS.map((value) => (
                <button
                  type="button"
                  key={value}
                  aria-pressed={use === value}
                  onClick={() => setUse(value)}
                >
                  {all.footageUse[value]}
                </button>
              ))}
            </div>
            {use !== "no" && (
              <div className="allowance">
                <label className="field" htmlFor="footage-budget">
                  {t.allowance}
                </label>
                <div className="allow-input">
                  <input
                    id="footage-budget"
                    className="input"
                    type="number"
                    min={Math.max(1, Math.ceil(used))}
                    max={100000}
                    step="1"
                    value={limit}
                    onChange={(e) => setLimit(e.target.value)}
                    required
                  />
                  <span>{t.credits}</span>
                </div>
                <div className="presets">
                  {[2, 5, 10].map((clips) => {
                    const n = clips * MEDIA_CATALOG.video.credits;
                    return (
                      <button
                        type="button"
                        key={n}
                        className="chip"
                        aria-pressed={Number(limit) === n}
                        disabled={n < used}
                        onClick={() => setLimit(String(n))}
                      >
                        {t.preset(n)} <small>{t.clips(clips)}</small>
                      </button>
                    );
                  })}
                </div>
                {used > 0 && (
                  <small className="muted">
                    {t.used(
                      creditNumber(usage.spent),
                      creditNumber(usage.reserved),
                    )}
                  </small>
                )}
              </div>
            )}
          </div>
        </section>
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <div className="production-footer">
          <span className="mono">
            {t.available(wallet ? creditNumber(wallet.available) : "—")}
          </span>
          <Button className="primary" disabled={busy}>
            {busy ? t.saving : t.save}
          </Button>
        </div>
        {preview && <p className="muted small center">{t.preview}</p>}
      </form>
    </Modal>
  );
}

/** One choice among a few, each with what it means. */
function Cards<T extends string>({
  legend,
  options,
  value,
  onChange,
}: {
  legend: string;
  options: readonly Option<T>[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <fieldset className="choice-cards">
      <legend>{legend}</legend>
      {options.map(({ value: v, label, note, Icon }) => (
        <label key={v} className={value === v ? "on" : ""}>
          <input
            type="radio"
            name={legend}
            value={v}
            checked={value === v}
            onChange={() => onChange(v)}
          />
          <Icon size={16} />
          <b>{label}</b>
          <small>{note}</small>
        </label>
      ))}
    </fieldset>
  );
}
